/**
 * Push Notification Utility
 * Manages service worker registration and push subscriptions for PWA
 */

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface PlatformSupport {
  isPWA: boolean;
  isStandalone: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  isBrave: boolean;
  supportsPush: boolean;
  supportsNotifications: boolean;
  needsHomeScreenInstall: boolean;
  installPrompt: string | null;
  braveInstructions: string | null;
}

/**
 * Detects platform capabilities and PWA installation status
 */
export function detectPlatformSupport(): PlatformSupport {
  // Check if running in browser
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      isPWA: false,
      isStandalone: false,
      isIOS: false,
      isAndroid: false,
      isMobile: false,
      isBrave: false,
      supportsPush: false,
      supportsNotifications: false,
      needsHomeScreenInstall: false,
      installPrompt: null,
      braveInstructions: null,
    };
  }

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  // Detect Android
  const isAndroid = /Android/.test(navigator.userAgent);
  
  // Detect mobile (iOS or Android)
  const isMobile = isIOS || isAndroid;
  
  // Detect Brave browser
  const isBrave = (navigator as any).brave && typeof (navigator as any).brave.isBrave === 'function';

  // Check if running as standalone PWA
  const isStandalone = 
    ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone) || // iOS
    window.matchMedia('(display-mode: standalone)').matches; // Standard

  // Check if app is installed as PWA
  const isPWA = isStandalone || 
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches;

  // Check notification support
  const supportsNotifications = 'Notification' in window;

  // Check push support
  const supportsPush = 
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    supportsNotifications;

  // iOS requires home screen installation for push notifications
  const needsHomeScreenInstall = isIOS && !isStandalone;

  // Generate install prompt message
  let installPrompt: string | null = null;
  if (needsHomeScreenInstall) {
    installPrompt = 
      'To receive push notifications on iOS, please add this app to your home screen:\n' +
      '1. Tap the Share button\n' +
      '2. Select "Add to Home Screen"\n' +
      '3. Tap "Add"';
  } else if (isAndroid && !isPWA) {
    installPrompt = 
      'For the best experience with push notifications, install this app:\n' +
      '1. Tap the menu (⋮)\n' +
      '2. Select "Install app" or "Add to Home screen"';
  }
  
  // Generate Brave-specific instructions (desktop only)
  let braveInstructions: string | null = null;
  if (isBrave && !isMobile) {
    braveInstructions =
      'Brave requires Google push services to be enabled. To set up push notifications:\n' +
      '1. Open brave://settings/privacy\n' +
      '2. Turn ON "Use Google services for push messaging"\n' +
      '3. Reload this page\n' +
      '4. Clear any old notification registrations and try enabling again\n\n' +
      'Note: Shields and fingerprinting protection can remain enabled.';
  }

  return {
    isPWA,
    isStandalone,
    isIOS,
    isAndroid,
    isMobile,
    isBrave,
    supportsPush,
    supportsNotifications,
    needsHomeScreenInstall,
    installPrompt,
    braveInstructions,
  };
}

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Push] Service Worker not supported');
    return null;
  }

  try {
    // Register the service worker
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('[Push] Service Worker registered successfully:', registration.scope);

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;

    return registration;
  } catch (error) {
    console.error('[Push] Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Request notification permission from the user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('[Push] Notifications not supported');
    return 'denied';
  }

  // Check if permission is already granted
  if (Notification.permission === 'granted') {
    return 'granted';
  }

  // Request permission
  try {
    const permission = await Notification.requestPermission();
    console.log('[Push] Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('[Push] Failed to request notification permission:', error);
    return 'denied';
  }
}

/**
 * Check if push notifications are actually available (not just API support)
 * This tests if the browser will actually allow push subscriptions
 */
export async function checkPushAvailability(
  applicationServerKey?: string
): Promise<{ available: boolean; error?: string }> {
  try {
    if (!window.isSecureContext) {
      return { available: false, error: 'Push requires HTTPS or localhost' };
    }

    if (!('serviceWorker' in navigator)) {
      return { available: false, error: 'Service Worker not supported' };
    }

    if (!('PushManager' in window)) {
      return { available: false, error: 'Push API not supported' };
    }

    if (!('Notification' in window)) {
      return { available: false, error: 'Notifications API not supported' };
    }

    if (Notification.permission === 'denied') {
      return { available: false, error: 'Notification permission denied at browser level' };
    }

    const registration = await navigator.serviceWorker.ready;

    if (applicationServerKey) {
      const permissionState = await registration.pushManager.permissionState({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(applicationServerKey),
      });

      if (permissionState === 'denied') {
        return { available: false, error: 'Push permission denied' };
      }
    }

    return { available: true };
  } catch (error: any) {
    return { available: false, error: error?.message || 'Failed to check push availability' };
  }
}

/**
 * Subscribe to push notifications
 * @param applicationServerKey - VAPID public key from backend
 */
export async function subscribeToPushNotifications(
  applicationServerKey: string
): Promise<PushSubscriptionData | null> {
  try {
    // Pre-flight check with VAPID key
    const availability = await checkPushAvailability(applicationServerKey);
    if (!availability.available && availability.error) {
      throw new Error(availability.error);
    }

    // Register service worker
    const registration = await registerServiceWorker();
    if (!registration) {
      throw new Error('Service Worker registration failed');
    }

    // Request notification permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Create new subscription
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(applicationServerKey),
        } as PushSubscriptionOptionsInit);

        console.log('[Push] Created new push subscription:', subscription);
      } catch (subscribeError: any) {
        // Log comprehensive error details for debugging
        console.error('[Push] subscribe failed', {
          name: subscribeError?.name,
          message: subscribeError?.message,
          stack: subscribeError?.stack,
          isSecureContext: window.isSecureContext,
          notificationPermission: Notification.permission,
          userAgent: navigator.userAgent,
        });

        // Provide user-friendly error messages based on error type
        if (subscribeError.name === 'NotAllowedError') {
          const isBrave = (navigator as any).brave && typeof (navigator as any).brave.isBrave === 'function';
          const isMobile = /iPad|iPhone|iPod|Android/.test(navigator.userAgent);
          
          let errorMsg = 
            'Browser or site permissions are blocking push notifications. Please:\n' +
            '1. Check your browser settings for notification permissions\n' +
            '2. Ensure this site is allowed to send notifications';
          
          // Only show Brave desktop instructions on non-mobile
          if (isBrave && !isMobile) {
            errorMsg += '\n3. If using Brave desktop, enable push services in brave://settings/privacy';
          }
          
          throw new Error(errorMsg);
        } else if (subscribeError.name === 'AbortError') {
          const isBrave = (navigator as any).brave && typeof (navigator as any).brave.isBrave === 'function';
          const isMobile = /iPad|iPhone|iPod|Android/.test(navigator.userAgent);
          
          // Only show Brave-specific error on desktop
          if (isBrave && !isMobile) {
            throw new Error(
              'Brave push service connection failed. To fix:\n' +
              '1. Open brave://settings/privacy\n' +
              '2. Turn ON "Use Google services for push messaging"\n' +
              '3. Reload this page and try again'
            );
          }
          throw new Error(
            'Push service connection failed. This can happen when:\n' +
            '1. Browser privacy settings block push notifications\n' +
            '2. Network connection interrupted the registration\n' +
            '3. Try checking your browser\'s notification settings'
          );
        } else if (subscribeError.name === 'InvalidCharacterError' || subscribeError.name === 'InvalidAccessError') {
          throw new Error('Invalid push configuration. Please contact support.');
        }
        
        throw subscribeError;
      }
    } else {
      console.log('[Push] Using existing push subscription');
    }

    // Convert subscription to PushSubscriptionData format
    return convertSubscriptionToData(subscription);
  } catch (error) {
    console.error('[Push] Failed to subscribe to push notifications:', error);
    throw error; // Re-throw to let caller handle it
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      const successful = await subscription.unsubscribe();
      console.log('[Push] Unsubscribed from push notifications:', successful);
      return successful;
    }

    return true; // Already unsubscribed
  } catch (error) {
    console.error('[Push] Failed to unsubscribe from push notifications:', error);
    return false;
  }
}

/**
 * Get the current push subscription
 */
export async function getCurrentPushSubscription(): Promise<PushSubscriptionData | null> {
  try {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return null;
    }

    return convertSubscriptionToData(subscription);
  } catch (error) {
    console.error('[Push] Failed to get current push subscription:', error);
    return null;
  }
}

/**
 * Test push notification (shows a local notification)
 */
export async function testPushNotification(): Promise<boolean> {
  try {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      return false;
    }

    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification('ConvoPro Test Notification', {
      body: 'Push notifications are working! 🎉',
      icon: '/favicons/android-icon-192x192.png',
      badge: '/favicons/android-icon-96x96.png',
      tag: 'test-notification',
    });

    return true;
  } catch (error) {
    console.error('[Push] Failed to show test notification:', error);
    return false;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert base64 VAPID key to Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Convert PushSubscription to PushSubscriptionData format
 */
function convertSubscriptionToData(subscription: PushSubscription): PushSubscriptionData {
  const key = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');

  if (!key || !auth) {
    throw new Error('Push subscription missing encryption keys');
  }

  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(key),
      auth: arrayBufferToBase64(auth),
    },
  };
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}
